import { motion } from "framer-motion";

// ── RecipeStamp ─────────────────────────────────────────────────────
//
// Generalized vintage-badge mascot. Two variants in use today:
//   - AI:    "Recipes by Chadwick" robot (ai-recipe-book-filter.webp)
//   - Human: "Made with Love" mama (human-recipe-book-filter.webp)
//
// Each is shown in two places:
//   1. Top-right of the TOC when the matching filter is active.
//   2. Top-right of every recipe page whose source matches.
//
// The cold "AI" / "CUSTOM" text pills the recipe page used to show
// were replaced by these stamps because (per the user) "AI looks
// so impersonal — give it a face." Same logic applies to the human
// side now that there's a mama-mascot for it.
//
// SLAP entry — single tween with a back-ease curve. Replaces an
// earlier 4-keyframe sequence that had visible inflection points.
// One smooth scale + rotate motion ending in a tilted rest pose.
//
// `display: block` on the img kills the inline-baseline whitespace
// browsers add to <img> by default — that's what was reading as a
// "weird bounding box" around the mascot before. Both PNGs were
// verified fully alpha-transparent at the corners at compress time.
export default function RecipeStamp({ src, size = 88, style = {} }) {
  return (
    <motion.img
      src={src}
      alt=""
      aria-hidden
      // Single tween with a back-ease curve. Replaces the previous
      // 4-keyframe sequence which had visible inflection points (the
      // 0.90 → 1.05 → 1 scale recoil and the -4 → -10 → -8 rotation
      // reversal both stuttered). Single tween + cubic-bezier
      // overshoot reads as ONE smooth slap-and-settle motion: scale
      // ramps up past 1.0 briefly then eases to rest, rotate sweeps
      // monotonically from -22° to its final -8° tilt.
      initial={{ opacity: 0, scale: 0.55, rotate: -22 }}
      animate={{ opacity: 1, scale: 1, rotate: -8 }}
      exit={{
        opacity: 0, scale: 0.85, rotate: -4,
        transition: { duration: 0.18, ease: [0.32, 0.72, 0, 1] },
      }}
      transition={{
        // 380ms total. cubic-bezier(0.34, 1.56, 0.64, 1) is the
        // "back ease" — single overshoot built into the curve, no
        // multi-keyframe needed. The eye reads one smooth motion.
        duration: 0.38,
        ease: [0.34, 1.56, 0.64, 1],
      }}
      style={{
        display: "block",
        width: size, height: "auto",
        userSelect: "none",
        pointerEvents: "none",
        // Drop-shadow follows the alpha mask, so the cast falls
        // around the badge silhouette, not a rectangle.
        filter: "drop-shadow(0 6px 10px rgba(60,40,18,0.30))",
        transformOrigin: "top right",
        ...style,
      }}
    />
  );
}
